import client from '@/graphql/client'
import ListUsers from '@/graphql/queries/ListUsers'
import ListWorkflows from '@/graphql/queries/ListWorkflows'
import CreateUser from '@/graphql/mutations/CreateUser'
import UpdateUser from '@/graphql/mutations/UpdateUser'
import AddAdminNote from '@/graphql/mutations/AddAdminNote'
import GetCurrentUser from '@/graphql/queries/GetCurrentUser'
import GenerateApiKey from '@/graphql/mutations/GenerateApiKey'
import ListPlatformAdmins from '@/graphql/queries/ListPlatformAdmins'
import ListPendingInvites from '@/graphql/queries/ListPendingInvites'
import ResendInvite from '@/graphql/mutations/ResendInvite'
import GetUserOrgFootprint from '@/graphql/queries/GetUserOrgFootprint'
import AdminSetUserActive from '@/graphql/mutations/AdminSetUserActive'
import AdminDeleteUser from '@/graphql/mutations/AdminDeleteUser'
import AdminSetPlatformRole from '@/graphql/mutations/AdminSetPlatformRole'
import AdminInviteSuperAdmin from '@/graphql/mutations/AdminInviteSuperAdmin'

const UserSortableField = {
  REGISTRATION_DATE: 'REGISTRATION_DATE',
  LAST_LOGIN: 'LAST_LOGIN',
  LOGIN_COUNTER: 'LOGIN_COUNTER'
}

const SortOrder = {
  ASC: 'ASC',
  DESC: 'DESC'
}

let latestListUsersRequestId = 0

async function fetchRemainingNestedPages (users) {
  for (const user of users) {
    if (!user.organisations) {
      continue
    }

    for (const org of user.organisations) {
      if (!org.projects) {
        continue
      }

      await Promise.all(org.projects.map(async (project) => {
        if (project.workflows?.nextToken) {
          let nextToken = project.workflows.nextToken
          while (nextToken) {
            const pageRes = await client.query({
              operationName: 'listWorkflows',
              query: ListWorkflows,
              variables: { projectId: project.id, limit: 100, nextToken }
            }, { failSilently: true })
            const page = pageRes?.listWorkflows?.project?.workflows
            project.workflows.items = (project.workflows.items || []).concat(page?.items || [])
            nextToken = page?.nextToken
          }
          project.workflows.nextToken = null
        }
      }))
    }
  }
}

export const user = {
  namespaced: true,
  state: {
    user: null,
    users: null,
    platformAdmins: [],
    pendingInvites: [],
    nextTokenUsers: null,
    loadingUsers: false,
    searchTerm: '',
    sortBy: {
      field: UserSortableField.REGISTRATION_DATE,
      order: SortOrder.DESC
    }
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload
    },
    updateUser (state, payload) {
      state.user = payload
    },
    setUsers (state, { items, append = false }) {
      if (append) {
        state.users = [...(state.users || []), ...items]
      } else {
        state.users = items
      }
    },
    setNextTokenUsers (state, token) {
      state.nextTokenUsers = token
    },
    setLoadingUsers (state, isLoading) {
      state.loadingUsers = isLoading
    },
    setCurrentSearchTerm (state, term) {
      state.searchTerm = term
    },
    setPlatformAdmins (state, admins) {
      state.platformAdmins = admins || []
    },
    setPendingInvites (state, invites) {
      state.pendingInvites = invites || []
    },
    setUserActiveInList (state, { userId, isActive }) {
      const u = state.users?.find((x) => x.id === userId)
      if (u) {
        u.isActive = isActive
      }
    },
    removeUserFromList (state, userId) {
      if (state.users) {
        state.users = state.users.filter((x) => x.id !== userId)
      }
    },
    setCurrentSortBy (state, sortBy) {
      state.sortBy = sortBy
    },
    updateUserNotes (state, { userId, adminNotes }) {
      if (!state.users) {
        return
      }

      const userIndex = state.users.findIndex((user) => user.id === userId)

      if (userIndex !== -1) {
        state.users[userIndex].adminNotes = adminNotes
      }
    }
  },
  actions: {
    async createUser ({ commit }, input) {
      try {
        const res = await client.query({
          operationName: 'createUser',
          query: CreateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', res.createUser)
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while creating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async updateUser ({ commit }, input) {
      try {
        const res = await client.query({
          operationName: 'updateUser',
          query: UpdateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', res.updateUser)
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while updating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async addAdminNote ({ commit }, input) {
      try {
        const response = await client.query({
          operationName: 'addAdminNote',
          query: AddAdminNote,
          variables: input
        })

        const updatedUser = response.addAdminNote

        if (updatedUser && updatedUser.adminNotes) {
          commit('updateUserNotes', {
            userId: updatedUser.id,
            adminNotes: updatedUser.adminNotes
          })
        }
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Error adding note',
          timeout: 6000,
          color: 'red'
        }, { root: true })
      }
    },

    // SuperAdmin-only: platform admins (Cognito group members) for the
    // Registered Users role badge + filter. Non-fatal on error.
    async listPlatformAdmins ({ commit }) {
      try {
        const { listPlatformAdmins } = await client.query({
          operationName: 'listPlatformAdmins',
          query: ListPlatformAdmins
        })
        commit('setPlatformAdmins', listPlatformAdmins || [])
        return listPlatformAdmins
      } catch (e) {
        console.error('Failed to load platform admins', e)
        return []
      }
    },

    async listPendingInvites ({ commit }) {
      try {
        const { listPendingInvites } = await client.query({
          operationName: 'listPendingInvites',
          query: ListPendingInvites
        })
        commit('setPendingInvites', listPendingInvites || [])
        return listPendingInvites
      } catch (e) {
        console.error('Failed to load pending invites', e)
        return []
      }
    },

    async resendInvite ({ commit, dispatch }, { email }) {
      try {
        const { resendInvite } = await client.query({
          operationName: 'resendInvite',
          query: ResendInvite,
          variables: { email }
        })
        if (resendInvite?.resent) {
          const note = resendInvite.emailSent === false
            ? `Temporary password reset for ${email}, but the email could not be sent.`
            : `Invitation re-sent to ${email}`
          commit('snackbar/showMessage', { content: note }, { root: true })
        } else {
          commit('snackbar/showMessage', { content: `${email} has already logged in — nothing to resend.`, color: 'info' }, { root: true })
        }
        await dispatch('listPendingInvites')
        return resendInvite
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to resend invite', color: 'error' }, { root: true })
        console.error('Failed to resend invite', e)
        return null
      }
    },

    async getUserOrgFootprint (_, { userId }) {
      try {
        const { getUserOrgFootprint } = await client.query({
          operationName: 'getUserOrgFootprint',
          query: GetUserOrgFootprint,
          variables: { userId }
        })
        return getUserOrgFootprint || []
      } catch (e) {
        console.error('Failed to load user org footprint', e)
        return []
      }
    },

    async adminSetUserActive ({ commit }, { userId, email, isActive }) {
      try {
        await client.query({
          operationName: 'adminSetUserActive',
          query: AdminSetUserActive,
          variables: { userId, email, isActive }
        })
        commit('setUserActiveInList', { userId, isActive })
        commit('snackbar/showMessage', { content: isActive ? 'User reactivated' : 'User deactivated' }, { root: true })
        return true
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to update user', color: 'error' }, { root: true })
        console.error(e)
        return false
      }
    },

    async adminDeleteUser ({ commit }, { userId, email }) {
      try {
        await client.query({
          operationName: 'adminDeleteUser',
          query: AdminDeleteUser,
          variables: { userId, email }
        })
        commit('removeUserFromList', userId)
        commit('snackbar/showMessage', { content: 'User deleted' }, { root: true })
        return true
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to delete user', color: 'error' }, { root: true })
        console.error(e)
        return false
      }
    },

    async adminSetPlatformRole ({ commit, dispatch }, { email, group, grant }) {
      try {
        await client.query({
          operationName: 'adminSetPlatformRole',
          query: AdminSetPlatformRole,
          variables: { email, group, grant }
        })
        await dispatch('listPlatformAdmins')
        commit('snackbar/showMessage', { content: grant ? `Granted ${group}` : `Removed ${group}` }, { root: true })
        return true
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to update role', color: 'error' }, { root: true })
        console.error(e)
        return false
      }
    },

    async adminInviteSuperAdmin ({ commit, dispatch }, { email, firstname, lastname }) {
      try {
        await client.query({
          operationName: 'adminInviteSuperAdmin',
          query: AdminInviteSuperAdmin,
          variables: { email, firstname: firstname || null, lastname: lastname || null }
        })
        await dispatch('listPlatformAdmins')
        commit('snackbar/showMessage', { content: `Admin access granted to ${email}` }, { root: true })
        return true
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to add admin', color: 'error' }, { root: true })
        console.error(e)
        return false
      }
    },

    async getCurrentUser ({ commit }) {
      const res = await client.query({
        operationName: 'getCurrentUser',
        query: GetCurrentUser
      })
      const currentUser = res.getCurrentUser

      commit('setUser', currentUser)
      commit('github/setGithubAccounts', currentUser?.githubAccounts || [], { root: true })

      return res
    },

    async generateApiKey ({ commit, state }) {
      try {
        const res = await client.query({
          operationName: 'generateApiKey',
          query: GenerateApiKey
        })

        const apiKey = res.generateApiKey.apiKey

        commit('setUser', {
          ...state.user,
          hasMcpApiKey: true
        })

        commit('snackbar/showMessage', {
          content: 'API Key generated successfully! Make sure to copy it now - you won\'t be able to see it again.',
          timeout: 6000,
          color: 'success'
        }, { root: true })

        return apiKey
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Failed to generate API key. Please try again.',
          timeout: 6000,
          color: 'red'
        }, { root: true })

        return null
      }
    },

    async listUsers (
      { commit, dispatch, state },
      {
        limit = 100,
        nextToken = null,
        search = null,
        sortBy = null,
        append = false,
        fetchAllForSearch = false,
        _requestId
      } = {}
    ) {
      const requestId = _requestId ?? ++latestListUsersRequestId
      const isLatest = () => requestId === latestListUsersRequestId

      commit('setLoadingUsers', true)
      commit('setCurrentSortBy', sortBy)

      const isNewSearchInitiated = (search !== null && search !== state.searchTerm && !append)

      if (isNewSearchInitiated) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      } else if (search !== null && !append && !fetchAllForSearch) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      }

      commit('setCurrentSearchTerm', search || '')

      try {
        const variables = { limit, sortBy: state.sortBy }

        if (nextToken) {
          variables.nextToken = nextToken
        }
        if (search && search.trim() !== '') {
          variables.search = search.trim()
        }

        const res = await client.query({
          operationName: 'listUsers',
          query: ListUsers,
          variables
        })

        if (!isLatest()) {
          return
        }

        const listUsersData = res.listUsers

        if (listUsersData) {
          if (!fetchAllForSearch) {
            await fetchRemainingNestedPages(listUsersData.items)

            if (!isLatest()) {
              return
            }
          }
          commit('setUsers', { items: listUsersData.items, append: append || (fetchAllForSearch && nextToken) })
          commit('setNextTokenUsers', listUsersData.nextToken)

          if (fetchAllForSearch && listUsersData.nextToken) {
            await dispatch('listUsers', {
              limit,
              nextToken: listUsersData.nextToken,
              search,
              sortBy: state.sortBy,
              append: true,
              fetchAllForSearch: true,
              _requestId: requestId
            })
          }
        }
        return listUsersData
      } catch (error) {
        if (!isLatest()) {
          return
        }
        commit('snackbar/showMessage', {
          content: 'Error fetching users',
          timeout: 6000,
          color: 'red'
        }, { root: true })
        commit('setNextTokenUsers', null)
      } finally {
        if (isLatest() && (!fetchAllForSearch || !state.nextTokenUsers)) {
          commit('setLoadingUsers', false)
        }
      }
    }
  }
}

export { UserSortableField, SortOrder }
